import React from 'react';
import { Loader2 } from 'lucide-react';

/**
 * ErrorBoundary — catches unhandled errors in child component trees and renders
 * a fallback UI instead of white-screening the app.
 *
 * React requires class components for Error Boundaries (no hooks equivalent exists).
 *
 * Usage:
 *   <ErrorBoundary>
 *     <SomeComponent />
 *   </ErrorBoundary>
 *
 * Optional props:
 *   fallback   — custom fallback element (defaults to the built-in error card)
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
    this.handleReset = this.handleReset.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Log to console in dev; swap for a real error reporter (Sentry, etc.) later.
    console.error('[ErrorBoundary] Caught error:', error, info.componentStack);
  }

  handleReset() {
    this.setState({ hasError: false, error: null });
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-[#0F0F0F] px-4">
          <div className="text-center max-w-sm">
            <div className="w-12 h-12 rounded-full bg-red-900/30 flex items-center justify-center mx-auto mb-4">
              <span className="text-red-400 text-xl">!</span>
            </div>
            <h2 className="text-white text-lg font-semibold mb-2">Something went wrong</h2>
            <p className="text-gray-400 text-sm mb-6">
              An unexpected error occurred. Your data is safe — try refreshing.
            </p>
            <button
              onClick={this.handleReset}
              className="px-4 py-2 bg-[#C1FF00] text-black text-sm font-semibold rounded-lg hover:bg-[#d4ff4d] transition-colors"
            >
              Try again
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
